// Agent colours (Features 1, 20 Oct): the account's setting as the board reads it, and the colour an agent's words
// take while it is on. Off, nothing changes how anything looks.

import { agentColor, cleanAgentColors, type AgentColor, type AgentColors } from "../../shared/authors";
import { commit } from "./actions";
import { get, useStore } from "./store";
import { useShallow } from "zustand/react/shallow";
import { customShades, type ThemeDef } from "../themes/themes";

const OFF: AgentColors = { on: false, colors: {} };

/** The setting, kept on the account so every device shows the same. */
export function useAgentColors(): AgentColors {
  const raw = useStore((s) => s.workspace?.agentColors);
  return cleanAgentColors(raw) ?? OFF;
}

/** The colour of words this agent wrote, or none: the user's own, or the setting off. */
export function colorOf(by: string | null | undefined, s: AgentColors): AgentColor | null {
  return s.on && by ? agentColor(by, s) : null;
}

/** Turns the colours on or off, or sets one agent's. */
export function setAgentColors(patch: { on?: boolean; agent?: string; color?: AgentColor }) {
  const now = cleanAgentColors(get().workspace?.agentColors) ?? OFF;
  const next: AgentColors = { on: patch.on ?? now.on, colors: patch.agent && patch.color ? { ...now.colors, [patch.agent]: patch.color } : now.colors };
  commit([{ op: "workspace.put", patch: { agentColors: next } }], "Agent colors", { history: false });
}

/**
 * The colors the user chose for agents (Features 6), drawn as the named seven are: each one's variable, readable on the
 * theme in view, for the board's root, and its rules beside the named ones' (boards.css), for where words take it.
 */
export function useCustomAgentColors(def: Pick<ThemeDef, "mode" | "fg" | "tile">): { vars: Record<string, string>; css: string } {
  const custom = useStore(useShallow((s) => [...new Set(Object.values(cleanAgentColors(s.workspace?.agentColors)?.colors ?? {}).filter((c) => c.startsWith("x-")))]));
  return {
    vars: customShades(def, custom),
    css: custom.map((c) => `[data-agent-color="${c}"],.tc[data-color="${c}"]{color:var(--tc-${c})}`).join(""),
  };
}
